import { useReducer } from 'react';

import {
  FORM_FIELD_MAX,
  NPS_FIELD_DEFAULTS,
  type FeedbackForm,
  type FeedbackFormField,
  type FeedbackFormFieldKind,
  type FormRepostFrequency,
  type RatingDisplay,
} from '@playerbase/types';

// ---------------------------------------------------------------------------
// In-memory builder state for the form-builder page.
//
// The reducer owns:
//   - the metadata snapshot (title, description, channels, repost,
//     anonymous, submit response copy)
//   - the ordered field array, each with a stable client-side id
//   - a transient `lastError` flag so the UI can render helper text
//     (e.g. "5 fields max") without the action site needing to know
//     where to surface it
//
// The hook is deliberately scope-limited: it does NOT call RPCs, fetch,
// or know about Realtime. The page wires every state change through a
// debounced sync layer; on first mount the page seeds state from the
// server-loaded form + fields, then drives subsequent updates through
// the reducer alone. This keeps the reducer pure and trivially testable.
// ---------------------------------------------------------------------------

export type BuilderFieldKind = FeedbackFormFieldKind;

// Local field shape: we keep `clientId` so a freshly-added field can be
// reordered or edited before it has a server-assigned UUID. The page
// reconciles `clientId` <-> server `id` on save responses.
export interface BuilderField {
  // Stable for the lifetime of the field's appearance in the list.
  // Equals the server `id` once persisted; otherwise a generated string.
  clientId: string;
  // Server-side id, present after replace_feedback_form_fields returns
  // it. The reducer never touches this value - it's pass-through state
  // for the page to map back when applying server responses.
  serverId: string | null;
  kind: BuilderFieldKind;
  title: string;
  description: string | null;
  required: boolean;
  // Per-kind config, mirroring FeedbackFormFieldConfigSchema minus the
  // discriminator. A blank-from-the-side-panel field starts with
  // sensible defaults (rating: scale=5, select: one empty option).
  config: BuilderFieldConfig;
}

export type BuilderFieldConfig =
  | Record<string, never> // short_text | long_text | severity | nps
  | { display?: RatingDisplay } // rating; absent display = numbers
  | { trueLabel?: string; falseLabel?: string } // binary; absent = Yes/No defaults
  | { options: string[]; maxValues?: number }; // select; maxValues>1 = multi

export interface BuilderMetadata {
  // Portal label and notes - never shown to players.
  name: string;
  internalDescription: string;
  // Discord-facing modal title (45-char cap) + the optional copy that
  // appears under it and on the posted button-message.
  title: string;
  discordDescription: string;
  surveyChannelId: string | null;
  responseChannelId: string | null;
  repostFrequency: FormRepostFrequency;
  anonymous: boolean;
  submitResponseTitle: string;
  submitResponseDescription: string;
  // Post-submit recruit opt-in (Story 10B). Empty arrays = no prompt.
  recruitChannelIds: string[];
  recruitRoleIds: string[];
}

export type BuilderError =
  | 'field_cap_reached'
  | 'select_options_min_one'
  | 'select_options_max_25'
  | 'nps_already_present';

export interface BuilderState {
  metadata: BuilderMetadata;
  fields: BuilderField[];
  lastError: BuilderError | null;
}

// Re-exported from the shared types package so the bot, portal builder
// and any future caller agree on a single source of truth.
export const FIELD_CAP = FORM_FIELD_MAX;
export const SELECT_OPTION_MAX = 25;

type Action =
  | { type: 'reset'; state: BuilderState }
  | { type: 'updateMetadata'; patch: Partial<BuilderMetadata> }
  | { type: 'addField'; kind: BuilderFieldKind; clientId: string }
  | { type: 'removeField'; clientId: string }
  | { type: 'reorderFields'; fromIndex: number; toIndex: number }
  | { type: 'updateFieldTitle'; clientId: string; value: string }
  | { type: 'updateFieldDescription'; clientId: string; value: string | null }
  | { type: 'updateFieldRequired'; clientId: string; value: boolean }
  | { type: 'setRatingDisplay'; clientId: string; display: RatingDisplay | undefined }
  | { type: 'setBinaryLabel'; clientId: string; which: 'true' | 'false'; value: string | undefined }
  | { type: 'replaceSelectOption'; clientId: string; index: number; value: string }
  | { type: 'addSelectOption'; clientId: string }
  | { type: 'removeSelectOption'; clientId: string; index: number }
  | { type: 'setSelectMaxValues'; clientId: string; maxValues: number | undefined }
  | { type: 'applyServerIds'; ids: Array<{ clientId: string; serverId: string }> }
  | { type: 'clearError' };

// ---------------------------------------------------------------------------
// Helpers (kept pure; no useState / useReducer access).
// ---------------------------------------------------------------------------

export function defaultConfigForKind(kind: BuilderFieldKind): BuilderFieldConfig {
  switch (kind) {
    case 'rating':
      // No display key = numbers (the default mode). Studios opt into
      // emoji explicitly via the field config UI.
      return {};
    case 'select':
      return { options: [''] };
    case 'short_text':
    case 'long_text':
    case 'severity':
    case 'binary':
    case 'nps':
      return {};
  }
}

export function makeBlankField(kind: BuilderFieldKind, clientId: string): BuilderField {
  // NPS is fixed-shape: question copy and required flag are seeded from
  // the shared NPS_FIELD_DEFAULTS so the row in the DB matches what the
  // bot will render in Discord (with {gameName} resolved at modal-build
  // time).
  if (kind === 'nps') {
    return {
      clientId,
      serverId: null,
      kind,
      title: NPS_FIELD_DEFAULTS.title,
      description: NPS_FIELD_DEFAULTS.description,
      required: NPS_FIELD_DEFAULTS.required,
      config: defaultConfigForKind(kind),
    };
  }
  return {
    clientId,
    serverId: null,
    kind,
    title: '',
    description: null,
    required: false,
    config: defaultConfigForKind(kind),
  };
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr;
  if (from < 0 || from >= arr.length) return arr;
  if (to < 0 || to >= arr.length) return arr;
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  if (item === undefined) return arr;
  next.splice(to, 0, item);
  return next;
}

function patchField(
  fields: BuilderField[],
  clientId: string,
  apply: (field: BuilderField) => BuilderField,
): BuilderField[] {
  let touched = false;
  const next = fields.map((f) => {
    if (f.clientId !== clientId) return f;
    touched = true;
    return apply(f);
  });
  return touched ? next : fields;
}

// ---------------------------------------------------------------------------
// Reducer.
// ---------------------------------------------------------------------------

export function builderReducer(state: BuilderState, action: Action): BuilderState {
  switch (action.type) {
    case 'reset':
      return action.state;

    case 'updateMetadata':
      return {
        ...state,
        metadata: { ...state.metadata, ...action.patch },
        lastError: null,
      };

    case 'addField': {
      if (state.fields.length >= FIELD_CAP) {
        return { ...state, lastError: 'field_cap_reached' };
      }
      if (action.kind === 'nps' && state.fields.some((f) => f.kind === 'nps')) {
        return { ...state, lastError: 'nps_already_present' };
      }
      return {
        ...state,
        fields: [...state.fields, makeBlankField(action.kind, action.clientId)],
        lastError: null,
      };
    }

    case 'removeField': {
      const next = state.fields.filter((f) => f.clientId !== action.clientId);
      if (next.length === state.fields.length) return state;
      return { ...state, fields: next, lastError: null };
    }

    case 'reorderFields': {
      const next = moveItem(state.fields, action.fromIndex, action.toIndex);
      if (next === state.fields) return state;
      return { ...state, fields: next, lastError: null };
    }

    case 'updateFieldTitle':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => ({
          ...f,
          title: action.value,
        })),
        lastError: null,
      };

    case 'updateFieldDescription':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => ({
          ...f,
          description: action.value === null ? null : action.value,
        })),
        lastError: null,
      };

    case 'updateFieldRequired':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => ({
          ...f,
          required: action.value,
        })),
        lastError: null,
      };

    case 'setRatingDisplay':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'rating') return f;
          // undefined display = numbers; we drop the key entirely so
          // the wire payload stays minimal.
          return {
            ...f,
            config: action.display === undefined ? {} : { display: action.display },
          };
        }),
        lastError: null,
      };

    case 'setBinaryLabel':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'binary') return f;
          const config = f.config as { trueLabel?: string; falseLabel?: string };
          const key = action.which === 'true' ? 'trueLabel' : 'falseLabel';
          // undefined / empty = back to the Yes/No default; drop the key
          // so the wire payload only ships explicit overrides.
          const next: { trueLabel?: string; falseLabel?: string } = {
            ...config,
          };
          if (action.value === undefined || action.value.length === 0) {
            delete next[key];
          } else {
            next[key] = action.value;
          }
          return { ...f, config: next };
        }),
        lastError: null,
      };

    case 'replaceSelectOption':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'select') return f;
          const config = f.config as { options: string[] };
          const options = config.options.slice();
          if (action.index < 0 || action.index >= options.length) return f;
          options[action.index] = action.value;
          return { ...f, config: { options } };
        }),
        lastError: null,
      };

    case 'addSelectOption':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'select') return f;
          const config = f.config as { options: string[] };
          if (config.options.length >= SELECT_OPTION_MAX) return f;
          return { ...f, config: { options: [...config.options, ''] } };
        }),
        lastError: null,
      };

    case 'removeSelectOption':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'select') return f;
          const config = f.config as { options: string[]; maxValues?: number };
          if (config.options.length <= 1) {
            // Removing the last option: keep an empty placeholder so the
            // builder always has somewhere to type. Clear maxValues if
            // anyone bumped it; the field is back to single-option.
            return { ...f, config: { options: [''] } };
          }
          if (action.index < 0 || action.index >= config.options.length) return f;
          const options = config.options.slice();
          options.splice(action.index, 1);
          // Clamp maxValues to the new option count so we don't carry a
          // stale "pick 7" when only 5 options remain.
          const nextMax =
            config.maxValues !== undefined && config.maxValues > options.length
              ? options.length
              : config.maxValues;
          return {
            ...f,
            config:
              nextMax === undefined ? { options } : { options, maxValues: nextMax },
          };
        }),
        lastError: null,
      };

    case 'setSelectMaxValues':
      return {
        ...state,
        fields: patchField(state.fields, action.clientId, (f) => {
          if (f.kind !== 'select') return f;
          const config = f.config as { options: string[]; maxValues?: number };
          if (action.maxValues === undefined || action.maxValues <= 1) {
            // Single-select: drop the key entirely so the wire shape stays
            // minimal and the bot's modal builder uses Discord's default.
            return { ...f, config: { options: config.options } };
          }
          // Cap by both the option count (can't pick more than exist) and
          // Discord's 25 hard limit.
          const ceiling = Math.min(config.options.length, 25);
          const clamped = Math.max(1, Math.min(action.maxValues, ceiling));
          return {
            ...f,
            config:
              clamped <= 1
                ? { options: config.options }
                : { options: config.options, maxValues: clamped },
          };
        }),
        lastError: null,
      };

    case 'applyServerIds': {
      if (action.ids.length === 0) return state;
      const map = new Map(action.ids.map((i) => [i.clientId, i.serverId]));
      let touched = false;
      const next = state.fields.map((f) => {
        const serverId = map.get(f.clientId);
        if (serverId === undefined) return f;
        if (f.serverId === serverId) return f;
        touched = true;
        return { ...f, serverId };
      });
      if (!touched) return state;
      return { ...state, fields: next };
    }

    case 'clearError':
      if (state.lastError === null) return state;
      return { ...state, lastError: null };
  }
}

// ---------------------------------------------------------------------------
// Hook.
// ---------------------------------------------------------------------------

export interface UseFormBuilderState {
  state: BuilderState;
  reset(state: BuilderState): void;
  updateMetadata(patch: Partial<BuilderMetadata>): void;
  addField(kind: BuilderFieldKind, clientId: string): void;
  removeField(clientId: string): void;
  reorderFields(fromIndex: number, toIndex: number): void;
  updateFieldTitle(clientId: string, value: string): void;
  updateFieldDescription(clientId: string, value: string | null): void;
  updateFieldRequired(clientId: string, value: boolean): void;
  setRatingDisplay(clientId: string, display: RatingDisplay | undefined): void;
  setBinaryLabel(clientId: string, which: 'true' | 'false', value: string | undefined): void;
  replaceSelectOption(clientId: string, index: number, value: string): void;
  addSelectOption(clientId: string): void;
  removeSelectOption(clientId: string, index: number): void;
  setSelectMaxValues(clientId: string, maxValues: number | undefined): void;
  applyServerIds(ids: Array<{ clientId: string; serverId: string }>): void;
  clearError(): void;
}

export function useFormBuilderState(initial: BuilderState): UseFormBuilderState {
  const [state, dispatch] = useReducer(builderReducer, initial);

  return {
    state,
    reset: (s) => dispatch({ type: 'reset', state: s }),
    updateMetadata: (patch) => dispatch({ type: 'updateMetadata', patch }),
    addField: (kind, clientId) => dispatch({ type: 'addField', kind, clientId }),
    removeField: (clientId) => dispatch({ type: 'removeField', clientId }),
    reorderFields: (fromIndex, toIndex) =>
      dispatch({ type: 'reorderFields', fromIndex, toIndex }),
    updateFieldTitle: (clientId, value) =>
      dispatch({ type: 'updateFieldTitle', clientId, value }),
    updateFieldDescription: (clientId, value) =>
      dispatch({ type: 'updateFieldDescription', clientId, value }),
    updateFieldRequired: (clientId, value) =>
      dispatch({ type: 'updateFieldRequired', clientId, value }),
    setRatingDisplay: (clientId, display) =>
      dispatch({ type: 'setRatingDisplay', clientId, display }),
    setBinaryLabel: (clientId, which, value) =>
      dispatch({ type: 'setBinaryLabel', clientId, which, value }),
    replaceSelectOption: (clientId, index, value) =>
      dispatch({ type: 'replaceSelectOption', clientId, index, value }),
    addSelectOption: (clientId) => dispatch({ type: 'addSelectOption', clientId }),
    removeSelectOption: (clientId, index) =>
      dispatch({ type: 'removeSelectOption', clientId, index }),
    setSelectMaxValues: (clientId, maxValues) =>
      dispatch({ type: 'setSelectMaxValues', clientId, maxValues }),
    applyServerIds: (ids) => dispatch({ type: 'applyServerIds', ids }),
    clearError: () => dispatch({ type: 'clearError' }),
  };
}

// ---------------------------------------------------------------------------
// Seed helpers used by the page on first load + when realtime delivers a
// remote edit.
// ---------------------------------------------------------------------------

export function builderStateFromServer(
  form: FeedbackForm,
  fields: FeedbackFormField[],
): BuilderState {
  return {
    metadata: {
      name: form.name,
      internalDescription: form.internalDescription ?? '',
      title: form.title,
      discordDescription: form.discordDescription ?? '',
      surveyChannelId: form.surveyChannelId,
      responseChannelId: form.responseChannelId,
      repostFrequency: form.repostFrequency,
      anonymous: form.anonymous,
      submitResponseTitle: form.submitResponseTitle ?? '',
      submitResponseDescription: form.submitResponseDescription ?? '',
      recruitChannelIds: form.recruitChannelIds,
      recruitRoleIds: form.recruitRoleIds,
    },
    fields: fields.map((f) => ({
      clientId: f.id,
      serverId: f.id,
      kind: f.kind,
      title: f.title,
      description: f.description,
      required: f.required,
      config: f.config as BuilderFieldConfig,
    })),
    lastError: null,
  };
}
